import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { formatDate } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { completeTask, resendSelfDisclosure, sendSelfDisclosure, useSelfDisclosures } from '../api';
import { addDays, daysBetween, formatDayKey, todayKey } from '../format';
import type { SelfDisclosureRow, SendSelfDisclosureBody, SendSelfDisclosureResponse } from '../types';
import { EditorCard, PrimaryAction } from './EditorCard';
import { ChipGroup, DateTimeBoxes, EditorField } from './fields';
import { isNextStepRequired, isValidation, useNextStepGate, type EditorProps } from './shared';
import styles from './editor.module.css';

type Channel = 'email' | 'whatsapp';

/** Channel of a version in the version list; `referrer` = submitted through a partner's link (D-248). */
const SENT_VIA_LABELS: Record<NonNullable<SelfDisclosureRow['sentVia']>, string> = {
  email: 'E-Mail',
  whatsapp: 'WhatsApp',
  portal: 'Portal',
  referrer: 'Tippgeber',
};

const CHANNELS: ReadonlyArray<{ value: Channel; label: string }> = [
  { value: 'email', label: 'E-Mail' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

/**
 * E7 footnote (D-384): the calculation assumptions are frozen with the volume calculation at
 * submission (`assumptionsInForce`, Selbstauskunft 10), not when the link is sent — so the dialog
 * shows no assumptions.
 */
export const SELF_DISCLOSURE_FOOTNOTE =
  'Die Finanzierbarkeit wird berechnet, sobald die unterschriebene Selbstauskunft vorliegt.';

export interface SendBodyInput {
  contactId: string;
  channel: Channel;
  to: string;
  followUp: boolean;
  followUpDays: number | null;
  completesTaskId: string | null;
}

/** The body of `POST /self-disclosures` (channel, recipient, follow-up, the task the send completes). */
export function selfDisclosureSendBody(input: SendBodyInput): SendSelfDisclosureBody {
  const body: SendSelfDisclosureBody = {
    contactId: input.contactId,
    channel: input.channel,
    followUpDays: input.followUp && input.followUpDays !== null ? input.followUpDays : null,
  };
  if (input.to.trim()) body.to = input.to.trim();
  if (input.completesTaskId) body.completesTaskId = input.completesTaskId;
  return body;
}

/** Kontaktakte II.E7 „Selbstauskunft versenden": channel, recipient, validity, follow-up (D-384). */
export function SelfDisclosureEditor({ record, task, onClose, onDone }: EditorProps) {
  const data = useSelfDisclosures(record.id, true);
  const [channel, setChannel] = useState<Channel>(record.header.email ? 'email' : 'whatsapp');
  const [to, setTo] = useState(record.header.email ?? record.header.phone ?? '');
  const [followUp, setFollowUp] = useState(true);
  const [date, setDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<SendSelfDisclosureResponse | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (data.data && !date) setDate(addDays(todayKey(), data.data.followUpDaysDefault));
  }, [data.data, date]);

  useEffect(() => {
    setTo(channel === 'email' ? (record.header.email ?? '') : (record.header.phone ?? ''));
  }, [channel, record.header.email, record.header.phone]);

  const info = data.data;
  const latest = info?.items[0] ?? null;
  const linkOpen = latest !== null && (latest.status === 'sent' || latest.status === 'in_progress');
  const linkDays = info?.linkDays ?? 30;
  const followUpDays = date ? Math.max(1, daysBetween(todayKey(), date)) : null;
  const versionHint =
    latest === null
      ? 'Für diesen Kontakt gibt es noch keine Selbstauskunft. Der Link erzeugt Version 1.'
      : linkOpen
        ? `Version ${latest.version} ist noch offen (${latest.statusLabel}${latest.expiresAt ? `, gültig bis ${formatDate(latest.expiresAt)}` : ''}). Ein neuer Link verlängert dieselbe Version, die gespeicherten Angaben bleiben erhalten.`
        : `Version ${latest.version} bleibt erhalten. Ein neuer Link erzeugt Version ${latest.version + 1}, Vorversionen bleiben einsehbar.`;
  const taskTitle = `Nachfassen: Selbstauskunft ${record.header.name}`;

  function buildBody(withTask: boolean): SendSelfDisclosureBody {
    return selfDisclosureSendBody({
      contactId: record.id,
      channel,
      to,
      followUp,
      followUpDays,
      completesTaskId: withTask && task ? task.id : null,
    });
  }

  const gate = useNextStepGate(record.header.name, async (step) => {
    if (!task) return;
    const response = await sendSelfDisclosure(buildBody(false));
    await completeTask(task.id, { outcomeType: 'self_disclosure_sent', nextStep: step });
    setResult(response);
  });

  async function submit() {
    if (!to.trim()) {
      setError(
        channel === 'email'
          ? 'Der Kontakt hat keine E-Mail-Adresse.'
          : 'Der Kontakt hat keine Telefonnummer.',
      );
      return;
    }
    setPending(true);
    setError(null);
    try {
      const response =
        linkOpen && latest
          ? await resendSelfDisclosure(latest.id, { channel, to: to.trim() })
          : await sendSelfDisclosure(buildBody(true));
      setResult(response);
    } catch (cause) {
      if (isNextStepRequired(cause) && task) gate.open();
      else if (isValidation(cause)) setError(Object.values(cause.fields)[0] ?? cause.message);
      else setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  async function copy() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
    } catch {
      setError(
        'Der Link konnte nicht in die Zwischenablage kopiert werden — markieren Sie ihn und kopieren Sie ihn von Hand.',
      );
    }
  }

  const blocked = info !== undefined && !info.canSend && !linkOpen;

  return (
    <>
      <EditorCard
        tone="paper"
        dot="var(--info-fg)"
        title="Selbstauskunft versenden"
        subtitle="Token-Link, mehrstufig, zwischengespeichert"
        footnote={SELF_DISCLOSURE_FOOTNOTE}
        label="Selbstauskunft-Editor"
        onClose={result ? () => onDone({ toast: 'Selbstauskunft-Link versendet.' }) : onClose}
        primary={
          result ? (
            <PrimaryAction onClick={() => onDone({ toast: 'Selbstauskunft-Link versendet.' })}>
              Fertig
            </PrimaryAction>
          ) : (
            <PrimaryAction
              loading={pending}
              disabled={blocked || data.isPending}
              onClick={() => void submit()}
            >
              {linkOpen ? 'Neuen Link erzeugen' : 'Link versenden'}
            </PrimaryAction>
          )
        }
      >
        {data.isError ? <Banner tone="danger">{errorMessage(data.error)}</Banner> : null}
        {blocked && info?.blockedReason ? <Banner tone="warning">{info.blockedReason}</Banner> : null}
        {task && !result ? (
          <Banner tone="neutral">Der Versand erledigt die Aufgabe „{task.title}“.</Banner>
        ) : null}
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {result ? (
          <>
            <Banner tone="success" title="Selbstauskunft-Link versendet.">
              Version {result.selfDisclosure.version} · per {channel === 'email' ? 'E-Mail' : 'WhatsApp'} an{' '}
              {to}
              {result.selfDisclosure.expiresAt
                ? ` · gültig bis ${formatDate(result.selfDisclosure.expiresAt)}`
                : ''}
              . Der Link wird nur einmal angezeigt.
            </Banner>
            <div className={styles.linkBox}>
              <span className={styles.linkText}>{result.link}</span>
              <button type="button" className={styles.linkAction} onClick={() => void copy()}>
                {copied ? 'Kopiert' : 'Link kopieren'}
              </button>
            </div>
          </>
        ) : (
          <>
            <EditorField label="Versandweg">
              <ChipGroup label="Versandweg" options={CHANNELS} value={channel} onChange={setChannel} />
            </EditorField>
            <EditorField label="Empfänger">
              <input
                className={clsx(styles.input)}
                aria-label="Empfänger"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </EditorField>
            <EditorField label="Gültigkeit">
              <p className={styles.text}>
                {linkDays} Tage · bis {formatDate(addDays(todayKey(), linkDays))}
              </p>
            </EditorField>
            <EditorField label="Hinweis">
              <p className={styles.text}>{versionHint}</p>
            </EditorField>
            {info && info.items.length > 0 ? (
              <EditorField label="Versionen">
                <div className={styles.versionList}>
                  {info.items.map((item) => (
                    <Link
                      key={item.id}
                      to={`/contacts/${record.id}/self-disclosure/${item.id}`}
                      className={styles.versionRow}
                    >
                      <span>
                        Version {item.version} · {item.statusLabel}
                        {item.sentVia ? ` · ${SENT_VIA_LABELS[item.sentVia]}` : ''}
                      </span>
                      <span className={styles.versionMeta}>
                        {item.submittedBy
                          ? `Eingereicht von ${item.submittedBy.name}, ${item.submittedBy.email}`
                          : item.submittedAt
                            ? `eingereicht ${formatDate(item.submittedAt)}`
                            : item.sentAt
                              ? `gesendet ${formatDate(item.sentAt)}`
                              : ''}
                      </span>
                    </Link>
                  ))}
                </div>
              </EditorField>
            ) : null}
            {linkOpen ? null : (
              <EditorField label="Folgeschritt">
                <div className={styles.followUp}>
                  <label className={styles.followUpToggle}>
                    <input
                      type="checkbox"
                      className={styles.listInput}
                      checked={followUp}
                      onChange={(event) => setFollowUp(event.target.checked)}
                    />
                    <span
                      className={clsx(styles.followUpBox, followUp && styles.followUpBoxOn)}
                      aria-hidden="true"
                    >
                      {followUp ? '✓' : ''}
                    </span>
                    <span className={styles.followUpLabel}>Follow-up planen</span>
                  </label>
                  {followUp ? (
                    <>
                      <DateTimeBoxes
                        label="Follow-up"
                        date={date}
                        time=""
                        onDateChange={(key) => setDate(key || addDays(todayKey(), 1))}
                        onTimeChange={() => undefined}
                        withTime={false}
                        small
                      />
                      <span className={styles.dateText}>
                        Erzeugt: {taskTitle} · {formatDayKey(date)}, 09:00
                      </span>
                    </>
                  ) : null}
                </div>
              </EditorField>
            )}
          </>
        )}
      </EditorCard>
      {gate.dialog}
    </>
  );
}
